import type {
  DomainBlock,
  DomainDocument,
  EntityId,
  JsonValue,
  SaveReceipt,
} from "@note-app/contracts";
import { createReferenceUri, parseReferenceUri } from "@note-app/contracts";
import { InvalidStateError, ValidationError, type Clock, type IdGenerator } from "@note-app/domain";
import {
  EntityNotFoundError,
  OptimisticConcurrencyError,
  type UnitOfWork,
} from "@note-app/storage";

export type DocumentBlockTransferMode = "move" | "copy";

export interface DocumentBlockTransferInput {
  readonly sourceDocumentId: EntityId;
  readonly targetDocumentId: EntityId;
  readonly blockIds: readonly EntityId[];
  readonly mode: DocumentBlockTransferMode;
  readonly sourceExpectedRevision: number;
  readonly targetExpectedRevision: number;
}

export interface DocumentBlockTransferUndoToken {
  readonly sourceDocumentId: EntityId;
  readonly targetDocumentId: EntityId;
  readonly sourceDocumentBefore: DomainDocument;
  readonly targetDocumentBefore: DomainDocument;
  readonly sourceRevisionAfter: number;
  readonly targetRevisionAfter: number;
  readonly sourceChanged: boolean;
}

export interface DocumentBlockTransferResult {
  readonly sourceDocument: DomainDocument;
  readonly targetDocument: DomainDocument;
  readonly transferredBlockIds: readonly EntityId[];
  readonly sourceReceipt?: SaveReceipt;
  readonly targetReceipt: SaveReceipt;
  readonly undo: DocumentBlockTransferUndoToken;
}

export interface DocumentBlockTransferUndoResult {
  readonly sourceReceipt?: SaveReceipt;
  readonly targetReceipt: SaveReceipt;
}

interface PreparedTransfer {
  readonly sourceDocument: DomainDocument;
  readonly targetDocument: DomainDocument;
  readonly transferredBlockIds: readonly EntityId[];
}

/**
 * Moves or copies whole block subtrees between documents. Both documents are
 * persisted in one storage transaction so a move can never be half-applied.
 */
export class TransferDocumentBlocksService {
  public constructor(
    private readonly unitOfWork: UnitOfWork,
    private readonly clock: Clock,
    private readonly ids: IdGenerator,
  ) {}

  execute(input: DocumentBlockTransferInput): Promise<DocumentBlockTransferResult> {
    validateInput(input);
    return this.unitOfWork.transaction(async (repositories) => {
      const [sourceItem, targetItem, sourceStored, targetStored] = await Promise.all([
        repositories.items.get(input.sourceDocumentId),
        repositories.items.get(input.targetDocumentId),
        repositories.documents.load(input.sourceDocumentId),
        repositories.documents.load(input.targetDocumentId),
      ]);
      if (!sourceItem || sourceItem.kind !== "document" || !sourceStored) {
        throw new EntityNotFoundError("document", input.sourceDocumentId);
      }
      if (!targetItem || targetItem.kind !== "document" || !targetStored) {
        throw new EntityNotFoundError("document", input.targetDocumentId);
      }
      assertRevision(input.sourceDocumentId, input.sourceExpectedRevision, sourceStored.revision);
      assertRevision(input.targetDocumentId, input.targetExpectedRevision, targetStored.revision);

      const prepared = prepareDocumentBlockTransfer(
        sourceStored.document,
        targetStored.document,
        input.blockIds,
        input.mode,
        this.ids,
      );
      const committedAt = this.clock.now();
      const sourceReceipt = input.mode === "move"
        ? await repositories.documents.save(
            input.sourceDocumentId,
            prepared.sourceDocument,
            sourceStored.revision,
            committedAt,
          )
        : undefined;
      const targetReceipt = await repositories.documents.save(
        input.targetDocumentId,
        prepared.targetDocument,
        targetStored.revision,
        committedAt,
      );
      return {
        ...prepared,
        ...(sourceReceipt ? { sourceReceipt } : {}),
        targetReceipt,
        undo: {
          sourceDocumentId: input.sourceDocumentId,
          targetDocumentId: input.targetDocumentId,
          sourceDocumentBefore: sourceStored.document,
          targetDocumentBefore: targetStored.document,
          sourceRevisionAfter: sourceReceipt?.revision ?? sourceStored.revision,
          targetRevisionAfter: targetReceipt.revision,
          sourceChanged: input.mode === "move",
        },
      };
    });
  }

  undo(token: DocumentBlockTransferUndoToken): Promise<DocumentBlockTransferUndoResult> {
    return this.unitOfWork.transaction(async (repositories) => {
      const [sourceStored, targetStored] = await Promise.all([
        repositories.documents.load(token.sourceDocumentId),
        repositories.documents.load(token.targetDocumentId),
      ]);
      if (!sourceStored) throw new EntityNotFoundError("document", token.sourceDocumentId);
      if (!targetStored) throw new EntityNotFoundError("document", token.targetDocumentId);
      assertRevision(token.sourceDocumentId, token.sourceRevisionAfter, sourceStored.revision);
      assertRevision(token.targetDocumentId, token.targetRevisionAfter, targetStored.revision);
      const committedAt = this.clock.now();
      const sourceReceipt = token.sourceChanged
        ? await repositories.documents.save(
            token.sourceDocumentId,
            token.sourceDocumentBefore,
            sourceStored.revision,
            committedAt,
          )
        : undefined;
      const targetReceipt = await repositories.documents.save(
        token.targetDocumentId,
        token.targetDocumentBefore,
        targetStored.revision,
        committedAt,
      );
      return {
        ...(sourceReceipt ? { sourceReceipt } : {}),
        targetReceipt,
      };
    });
  }
}

export function prepareDocumentBlockTransfer(
  sourceDocument: DomainDocument,
  targetDocument: DomainDocument,
  blockIds: readonly EntityId[],
  mode: DocumentBlockTransferMode,
  ids: IdGenerator,
): PreparedTransfer {
  const selectedIds = new Set(blockIds);
  const extracted: DomainBlock[] = [];
  const sourceBlocks = extractSelectedBlocks(sourceDocument.blocks, selectedIds, extracted);
  if (extracted.length === 0) {
    throw new ValidationError([{ path: "blockIds", message: "没有找到可转移的内容块。" }]);
  }
  const foundIds = new Set<string>();
  visitBlocks(extracted, (block) => foundIds.add(block.id));
  const missing = blockIds.filter((id) => !foundIds.has(id));
  if (missing.length > 0) {
    throw new ValidationError([{ path: "blockIds", message: `内容块不存在：${missing.join(", ")}` }]);
  }

  const transferred = mode === "copy"
    ? cloneDocumentBlocksWithFreshIds(extracted, ids)
    : extracted;
  return {
    sourceDocument: mode === "move"
      ? { ...sourceDocument, blocks: sourceBlocks }
      : sourceDocument,
    targetDocument: {
      ...targetDocument,
      blocks: [...targetDocument.blocks, ...transferred],
    },
    transferredBlockIds: transferred.map((block) => block.id),
  };
}

function extractSelectedBlocks(
  blocks: readonly DomainBlock[],
  selectedIds: ReadonlySet<string>,
  extracted: DomainBlock[],
): DomainBlock[] {
  const remaining: DomainBlock[] = [];
  for (const block of blocks) {
    if (selectedIds.has(block.id)) {
      extracted.push(block);
      continue;
    }
    const children = extractSelectedBlocks(block.children, selectedIds, extracted);
    remaining.push(children === block.children ? block : { ...block, children });
  }
  return remaining;
}

/**
 * Creates an independent block tree while preserving links between blocks in
 * that tree. Item and attachment references intentionally remain unchanged.
 */
export function cloneDocumentBlocksWithFreshIds(
  blocks: readonly DomainBlock[],
  ids: IdGenerator,
): DomainBlock[] {
  const idMap = new Map<string, string>();
  visitBlocks(blocks, (block) => idMap.set(block.id, ids.next()));
  const clone = (block: DomainBlock): DomainBlock => ({
    ...block,
    id: idMap.get(block.id)!,
    props: rewriteBlockReferences(block.props, idMap) as Record<string, JsonValue>,
    content: rewriteBlockReferences(block.content, idMap),
    children: block.children.map(clone),
  });
  return blocks.map(clone);
}

function rewriteBlockReferences(value: JsonValue, idMap: ReadonlyMap<string, string>): JsonValue {
  if (typeof value === "string") {
    const target = parseReferenceUri(value);
    if (target?.kind === "block" && idMap.has(target.id)) {
      return createReferenceUri({ kind: "block", id: idMap.get(target.id)! });
    }
    return value;
  }
  if (Array.isArray(value)) return value.map((entry) => rewriteBlockReferences(entry, idMap));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, rewriteBlockReferences(entry, idMap)]),
    );
  }
  return value;
}

function visitBlocks(blocks: readonly DomainBlock[], visit: (block: DomainBlock) => void): void {
  for (const block of blocks) {
    visit(block);
    visitBlocks(block.children, visit);
  }
}

function validateInput(input: DocumentBlockTransferInput): void {
  if (input.sourceDocumentId === input.targetDocumentId) {
    throw new InvalidStateError("源文档和目标文档不能相同。");
  }
  if (input.blockIds.length === 0) {
    throw new ValidationError([{ path: "blockIds", message: "请先选择至少一个内容块。" }]);
  }
}

function assertRevision(entityId: string, expected: number, actual: number): void {
  if (expected !== actual) throw new OptimisticConcurrencyError(entityId, expected, actual);
}
